import React from 'react';
import { Box, Typography } from '@mui/material';

const privacyPolicyStyles = {
    container: {
        padding: '20px',
        backgroundColor: '#f7f7f7',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        maxWidth: '800px',
        margin: '40px auto',
        lineHeight: '1.6',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        overflow: 'auto',
    },
    header: {
        marginBottom: '20px',
        borderBottom: '1px solid #e1e1e1',
        paddingBottom: '10px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#333',
    },
    section: {
        marginBottom: '20px',
    },
    sectionTitle: {
        fontWeight: 'bold',
        color: '#333',
        marginBottom: '10px',
    },
    paragraph: {
        marginBottom: '10px',
        color: '#292828',
    },
};

const CookiePolicy = () => {
    return (
        <Box style={privacyPolicyStyles.container}>
            <Box style={privacyPolicyStyles.header}>
                <Typography variant="h1" style={privacyPolicyStyles.title}>Cookie Policy</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>Updated: August 13th, 2024</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    To ensure clarity in this Cookie Policy, we define the following terms as follows:
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>1. Definitions and Key
                    Terms</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    To ensure clarity in this Cookie Policy, we define the following terms as follows:
                </Typography>
                <ul style={{ color: '#292828' }}>
                    <li><strong>Cookie:</strong> A small data file generated by a website and stored by your web
                        browser. It helps to identify your browser, provides analytics, and remembers information like
                        your language preferences or login details.
                    </li>
                    <li><strong>Company:</strong> Refers to Connexxtion, mentioned as "Company," "we," "us," or "our,"
                        responsible for your information under this Cookie Policy.
                    </li>
                    <li><strong>Device:</strong> Any internet-connected device such as a phone, tablet, computer, or any
                        other device that can access Connexxtion and its services.
                    </li>
                    <li><strong>Personal Data:</strong> Any information that directly, indirectly, or in connection with
                        other data allows for the identification of a natural person.
                    </li>
                    <li><strong>Service:</strong> The service provided by Connexxtion, as described in the relevant
                        terms and on this platform.
                    </li>
                    <li><strong>Third-Party Service:</strong> Refers to advertisers, sponsors, marketing partners, and
                        others who provide our content or whose products/services we think may interest you.
                    </li>
                    <li><strong>Website:</strong> Connexxtion's site, accessible via <a
                        href="https://connexxtion.ch">https://connexxtion.ch</a>.
                    </li>
                    <li><strong>You:</strong> An individual or entity registered with Connexxtion to use the Services.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>2. Introduction</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    This Cookie Policy explains how Connexxtion and its affiliates (collectively "Connexxtion," "we," "us," and "our")
                    use cookies and similar technologies to recognize you when you visit our website, including&nbsp;
                    <a style={{ color: '#292828' }} href="https://connexxtion.ch">https://connexxtion.ch</a>
                    &nbsp;related URLs, mobile versions, and sub-domains ("Websites"). It also explains these technologies, why we use them, and how you can control them.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>3. What is a Cookie?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    A cookie is a small text file stored on your computer or other internet-connected device to identify your browser, provide analytics,
                    and remember your preferences such as language settings or login information. Cookies are safe and cannot run programs or deliver viruses to your device.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>4. Why Do We Use Cookies?</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We use first-party and/or third-party cookies for various purposes, including:
                </Typography>
                <ul style={{ color: '#292828' }}>
                    <li>Facilitating the operation and functionality of our website.</li>
                    <li>Enhancing your experience on our website, making navigation quicker and easier.</li>
                    <li>Creating a personalized user experience and understanding what content is most useful or
                        interesting to you.
                    </li>
                    <li>Analyzing website usage to customize it for optimal performance.</li>
                    <li>Identifying future prospects and personalizing marketing and sales interactions.</li>
                    <li>Tailoring online advertising to your interests.</li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>5. Types of Cookies Connexxtion
                    Uses</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Cookies can be session cookies or persistent cookies:
                </Typography>
                <ul style={{ color: '#292828' }}>
                    <li><strong>Session Cookies:</strong> Expire automatically when you close your browser.</li>
                    <li><strong>Persistent Cookies:</strong> Remain until they expire or are deleted. Expiration dates
                        are set within the cookies themselves, ranging from a few minutes to several years.
                    </li>
                </ul>
                <Typography style={{ color: '#292828' }}>We use the following types of cookies:</Typography>
                <ul style={{ color: '#292828' }}>
                    <li><strong>Essential Cookies:</strong> Necessary for the website to function. These cookies enable
                        core features such as security, network management, and accessibility. Disabling them may affect
                        how the website functions.
                    </li>
                    <li><strong>Performance and Functionality Cookies:</strong> Enhance the website's performance and
                        functionality but are not essential. Without these cookies, features like video playback may be
                        unavailable, or you may need to log in each time you visit.
                    </li>
                    <li><strong>Social Media Cookies:</strong> Used when you share content using social media buttons or
                        engage with our content on platforms like Facebook, Twitter, or Google+. These cookies track
                        conversions, optimize ads, build targeted audiences, and remarket to users.
                    </li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>6. Managing Cookies</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    Most browsers allow you to control cookies through their 'settings' preferences. However, limiting
                    cookies may impact your user experience and prevent you from saving customized settings like login
                    information.
                    For more details on managing cookies, refer to your browser's help menu:
                </Typography>
                <ul style={{ color: '#292828' }}>
                    <li>Google Chrome</li>
                    <li>Internet Explorer</li>
                    <li>Mozilla Firefox</li>
                    <li>Safari (Desktop)</li>
                    <li>Safari (Mobile)</li>
                    <li>Android Browser</li>
                    <li>Opera</li>
                    <li>Opera Mobile</li>
                </ul>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>7. Blocking and Disabling Cookies</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    You may block cookies via your browser settings, but this may affect the website's functionality and limit your ability to use certain features.
                    Blocking cookies does not delete them; you must delete them from within your browser settings.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>8. Changes to Our Cookie Policy</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    We may update our Cookie Policy to reflect changes in our service or policies. If changes are made, we will notify you (e.g., through our Service)
                    and provide an opportunity to review the updated policy before it takes effect.
                    If you continue using our service, you agree to the updated policy. If not, you can delete your account.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>9. Your Consent</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    By using our website, registering an account, or making a purchase, you consent to our Cookie Policy and agree to its terms.
                </Typography>
            </Box>
            <Box style={privacyPolicyStyles.section}>
                <Typography variant="h2" style={privacyPolicyStyles.sectionTitle}>10. Contact Us</Typography>
                <Typography style={privacyPolicyStyles.paragraph}>
                    If you have any questions regarding our Cookie Policy, please contact us:
                </Typography>
                <p style={privacyPolicyStyles.paragraph}>Email: <a href="mailto:support@connexxtion.ch">support@connexxtion.ch</a></p>
            </Box>
        </Box>
    );
};

export default CookiePolicy;
